import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success("Nachricht gesendet!", {
        description: "Wir melden uns schnellstmöglich bei Ihnen."
      });

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-zinc-50 py-16 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight mb-4">
              Kontakt
            </h1>
            <p className="text-lg text-zinc-600 max-w-2xl">
              Haben Sie Fragen zur Anhänger-Miete oder möchten Sie einen Termin vereinbaren? 
              Kontaktieren Sie uns – wir helfen Ihnen gerne weiter.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-xl font-bold text-zinc-950 mb-6">Kontaktdaten</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 mb-1">Adresse</p>
                    <p className="text-zinc-600 text-sm">
                      Wagnerweg 8<br />
                      4202 Sonnberg im Mühlkreis<br />
                      Österreich
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 mb-1">Telefon</p>
                    <a 
                      href="tel:+436764138585" 
                      className="text-zinc-600 text-sm hover:text-orange-600 transition-colors"
                    >
                      +43 676 4138585
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 mb-1">E-Mail</p>
                    <a 
                      href="mailto:info@rentmich.at" 
                      className="text-zinc-600 text-sm hover:text-orange-600 transition-colors"
                    >
                      info@rentmich.at
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 mb-1">Abholzeiten</p>
                    <p className="text-zinc-600 text-sm">Nach Vereinbarung</p>
                  </div>
                </div>
              </div>

              {/* Region Info */}
              <div className="mt-8 p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                <p className="text-sm text-zinc-600">
                  <strong className="text-zinc-900">Abholstandort:</strong> Sonnberg im Mühlkreis, 
                  Urfahr-Umgebung, Oberösterreich
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="bg-zinc-50 rounded-2xl p-8 border border-zinc-100">
                <h2 className="text-xl font-bold text-zinc-950 mb-2">Nachricht senden</h2>
                <p className="text-zinc-600 mb-6">
                  Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        required
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="Max"
                        className="border-zinc-200 bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        required
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Mustermann"
                        className="border-zinc-200 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="max@beispiel.at"
                        className="border-zinc-200 bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+43 ..."
                        className="border-zinc-200 bg-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Ihre Nachricht an uns..."
                      className="border-zinc-200 bg-white resize-none"
                      rows={5}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Nachricht senden
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
